import { EssDiagnosticEvent } from './ess-diagnostic-event';

export interface StoredEssDiagnostic {
    eventId: string;
    event: EssDiagnosticEvent;
    destination: string;
    appId: string;
    appVersion: string;
    enqueueOrder?: number;
}

export interface EssDiagnosticStore {
    save(records: StoredEssDiagnostic[]): Promise<void>;
    claim(destination: string, owner: string, now: number): Promise<StoredEssDiagnostic[]>;
    acknowledge(destination: string, owner: string, ids: string[], now: number): Promise<void>;
    release(destination: string, owner: string, now: number): Promise<void>;
}

/** Every modification is atomic across tabs. Committed events survive browser restart. */
export class IndexedDbEssDiagnosticStore implements EssDiagnosticStore {
    private database: Promise<IDBDatabase>;
    constructor(private factory?: IDBFactory, private ranges?: typeof IDBKeyRange,
                private name = 'ess-connection-diagnostics') {}

    private open(): Promise<IDBDatabase> {
        if (!this.database) {
            this.database = new Promise<IDBDatabase>((resolve, reject) => {
                const factory = this.factory || (typeof indexedDB === 'undefined' ? null : indexedDB);
                if (!factory) { reject(new Error('IndexedDB unavailable')); return; }
                const request = factory.open(this.name, 1);
                let failed = false;
                request.onupgradeneeded = () => {
                    const events = request.result.createObjectStore('events', { keyPath: 'eventId' });
                    events.createIndex('destinationOrder', ['destination', 'enqueueOrder'], { unique: true });
                    request.result.createObjectStore('state', { keyPath: 'id' });
                };
                request.onerror = () => { failed = true; reject(request.error); };
                request.onblocked = () => { failed = true; reject(new Error('IndexedDB upgrade blocked')); };
                request.onsuccess = () => {
                    const db = request.result;
                    if (failed) { db.close(); return; }
                    db.onversionchange = () => { db.close(); this.database = null; };
                    resolve(db);
                };
            }).catch(error => { this.database = null; throw error; });
        }
        return this.database;
    }

    private async transaction<T>(work: (events: IDBObjectStore, state: IDBObjectStore, result: (value: T) => void) => void): Promise<T> {
        const db = await this.open();
        return new Promise<T>((resolve, reject) => {
            const tx = db.transaction(['events', 'state'], 'readwrite');
            let value: T;
            tx.oncomplete = () => resolve(value);
            tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
            try { work(tx.objectStore('events'), tx.objectStore('state'), result => { value = result; }); }
            catch (error) { tx.abort(); reject(error); }
        });
    }

    /** Clear only this feature's records and delivery leases, atomically across tabs. */
    clear(): Promise<void> {
        return this.transaction<void>((events, state) => { events.clear(); state.clear(); });
    }

    save(records: StoredEssDiagnostic[]): Promise<void> {
        return this.transaction<void>((events, state) => {
            const counter = state.get('order');
            counter.onsuccess = () => {
                let order = counter.result ? counter.result.value : 0;
                let index = 0;
                const next = () => {
                    if (index === records.length) { return; }
                    const record = records[index++];
                    let stored = record;
                    const existing = events.get(record.eventId);
                    existing.onsuccess = () => {
                        if (existing.result) { next(); return; }
                        const insert = () => {
                            events.add(Object.assign({}, stored, { enqueueOrder: ++order }));
                            state.put({ id: 'order', value: order });
                            next();
                        };
                        if (record.event.source === 'collector') {
                            const key = 'sequence:' + record.event.sessionId + ':' + record.event.attemptId + ':' + (record.event.collectorEpoch || 'legacy');
                            const sequence = state.get(key);
                            sequence.onsuccess = () => {
                                const previous = sequence.result ? sequence.result.value : 0;
                                const nextSequence = record.event.sequence || previous + 1;
                                stored = Object.assign({}, record, { event: Object.assign({}, record.event, { sequence: nextSequence }) });
                                state.put({ id: key, value: Math.max(previous, nextSequence) });
                                insert();
                            };
                        } else { insert(); }
                    };
                };
                next();
            };
        });
    }

    claim(destination: string, owner: string, now: number): Promise<StoredEssDiagnostic[]> {
        return this.transaction<StoredEssDiagnostic[]>((events, state, result) => {
            const key = 'lease:' + destination;
            const lease = state.get(key);
            lease.onsuccess = () => {
                const previous = lease.result || { id: key, failures: 0, nextSend: 0, expiresAt: 0 };
                const leased = previous.expiresAt > now && previous.expiresAt <= now + 30000;
                const waiting = previous.nextSend > now && previous.nextSend <= now + 60000;
                // A clock moved backwards must not strand records behind an impossible deadline.
                if (leased || waiting) { result([]); return; }
                const records: StoredEssDiagnostic[] = [];
                const ranges = this.ranges || IDBKeyRange;
                const cursor = events.index('destinationOrder').openCursor(ranges.bound([destination, 0], [destination, Number.MAX_SAFE_INTEGER]));
                cursor.onsuccess = () => {
                    const current = cursor.result;
                    if (current && records.length < 25) {
                        records.push(current.value);
                        current.continue();
                    } else {
                        if (records.length) { state.put(Object.assign({}, previous, { owner, expiresAt: now + 30000 })); }
                        result(records);
                    }
                };
            };
        });
    }

    acknowledge(destination: string, owner: string, ids: string[], now: number): Promise<void> {
        return this.transaction<void>((events, state) => {
            ids.forEach(id => {
                const record = events.get(id);
                record.onsuccess = () => {
                    if (record.result && record.result.destination === destination) { events.delete(id); }
                };
            });
            const lease = state.get('lease:' + destination);
            lease.onsuccess = () => {
                if (lease.result && lease.result.owner === owner) {
                    state.put(Object.assign({}, lease.result, { owner: null, expiresAt: 0, failures: 0, nextSend: now + 5000 }));
                }
            };
        });
    }

    release(destination: string, owner: string, now: number): Promise<void> {
        return this.transaction<void>((_, state) => {
            const lease = state.get('lease:' + destination);
            lease.onsuccess = () => {
                if (!lease.result || lease.result.owner !== owner) { return; }
                const failures = lease.result.failures || 0;
                state.put(Object.assign({}, lease.result, { owner: null, expiresAt: 0,
                    failures: failures + 1, nextSend: now + [10000, 30000, 60000][Math.min(failures, 2)] }));
            };
        });
    }

    async close(): Promise<void> {
        if (this.database) { (await this.database).close(); this.database = null; }
    }
}
